import { useMemo } from 'react'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import FormatColorFill from '@mui/icons-material/FormatColorFill'
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined'
import FormatBold from '@mui/icons-material/FormatBold'
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenter from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRight from '@mui/icons-material/FormatAlignRight'
import type { DesignShape, DesignStroke } from '../../types/design'
import type { DesignEditorApi } from './useDesignEditor'
import { ColorPicker } from '../Common'
import { IconButton } from '../Common/IconButton'

type BoxRect = { x: number; y: number; width: number; height: number }
type Viewport = { x: number; y: number; zoom: number }

type Props = {
  editor: DesignEditorApi
  selectionBounds: BoxRect | null
  viewport: Viewport
  hostSize: { w: number; h: number }
}

/**
 * Canva-style floating toolbar. Appears above the selection with 4-6 quick
 * actions tailored to the shape type. Falls back to a top-docked position if
 * the selection sits too close to the top of the viewport.
 */
export const FloatingToolbar = ({ editor, selectionBounds, viewport, hostSize }: Props) => {
  const { state, updateShapes, deleteShapes, duplicate } = editor
  const selection = state.selection
  const selected = useMemo(
    () => state.shapes.filter((s) => selection.includes(s.id)),
    [state.shapes, selection],
  )

  if (!selectionBounds || selected.length === 0) return null

  const single = selected.length === 1 ? selected[0] : null

  // World → screen inside the .dh-canvas-host (which contains this component
  // positioned absolutely). The svg viewBox uses the same viewport math.
  const screenX = viewport.x + selectionBounds.x * viewport.zoom
  const screenY = viewport.y + selectionBounds.y * viewport.zoom
  const screenW = selectionBounds.width * viewport.zoom

  // Prefer placement above the selection; if there's not enough room, dock
  // below the top edge of the canvas host.
  const TOOLBAR_HEIGHT = 40
  const MARGIN = 12
  const wantTop = screenY - TOOLBAR_HEIGHT - MARGIN
  const top = wantTop < 8 ? Math.min(hostSize.h - TOOLBAR_HEIGHT - 8, screenY + selectionBounds.height * viewport.zoom + MARGIN) : wantTop
  const centerX = screenX + screenW / 2
  const left = Math.max(8, Math.min(hostSize.w - 8, centerX))

  const patchAll = (patch: Partial<DesignShape>) => {
    updateShapes(selection.map((id) => ({ id, patch })))
  }

  const supportsFill = single && single.fill.kind !== undefined && !['LINE', 'ARROW', 'POLYLINE', 'CURVE', 'FREEHAND', 'CONNECTOR', 'IMAGE', 'CHART'].includes(single.type)
  const supportsStroke = single && !['TEXT', 'STICKY', 'IMAGE', 'CHART'].includes(single.type)
  const isText = single && (single.type === 'TEXT' || single.type === 'STICKY')

  const currentFill = single && single.fill.kind === 'SOLID' ? single.fill.color : '#38bdf8' // mosaic-keep: user data
  const currentStroke = single?.stroke?.color ?? '#38bdf8' // mosaic-keep: user data
  const currentTextColor = single?.textStyle?.color ?? '#f8fafc' // mosaic-keep: user data
  const currentAlign = single?.textStyle?.align ?? 'CENTER'

  return (
    <div
      className="dh-floating-toolbar"
      style={{ left: `${left}px`, top: `${Math.max(8, top)}px`, transform: 'translateX(-50%)' }}
      role="toolbar"
      aria-label="Selection actions"
    >
      {isText && single && (
        <>
          <ColorSwatch
            title="Text colour"
            value={currentTextColor}
            onChange={(color) => patchAll({ textStyle: { ...(single.textStyle ?? { fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, fontWeight: 500, align: 'CENTER', color, lineHeight: 1.35, letterSpacing: 0 }), color } })}
          />
          <ToolbarButton
            title="Bold"
            active={(single.textStyle?.fontWeight ?? 500) >= 700}
            onClick={() => {
              const style = single.textStyle ?? { fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, fontWeight: 500, align: 'CENTER', color: '#f8fafc', lineHeight: 1.35, letterSpacing: 0 } // mosaic-keep: user data
              patchAll({ textStyle: { ...style, fontWeight: style.fontWeight >= 700 ? 500 : 700 } })
            }}
          >
            <FormatBold fontSize="small" />
          </ToolbarButton>
          <div className="dh-ft-divider" />
          <ToolbarButton title="Align left" active={currentAlign === 'LEFT'} onClick={() => single.textStyle && patchAll({ textStyle: { ...single.textStyle, align: 'LEFT' } })}>
            <FormatAlignLeft fontSize="small" />
          </ToolbarButton>
          <ToolbarButton title="Align centre" active={currentAlign === 'CENTER'} onClick={() => single.textStyle && patchAll({ textStyle: { ...single.textStyle, align: 'CENTER' } })}>
            <FormatAlignCenter fontSize="small" />
          </ToolbarButton>
          <ToolbarButton title="Align right" active={currentAlign === 'RIGHT'} onClick={() => single.textStyle && patchAll({ textStyle: { ...single.textStyle, align: 'RIGHT' } })}>
            <FormatAlignRight fontSize="small" />
          </ToolbarButton>
          <div className="dh-ft-divider" />
        </>
      )}
      {supportsFill && !isText && (
        <ColorSwatch
          title="Fill colour"
          value={currentFill}
          icon={<FormatColorFill fontSize="small" />}
          onChange={(color) => patchAll({ fill: { kind: 'SOLID', color } })}
        />
      )}
      {supportsStroke && (
        <ColorSwatch
          title="Stroke colour"
          value={currentStroke}
          icon={<BorderColorOutlined fontSize="small" />}
          onChange={(color) => {
            const existing: DesignStroke = single?.stroke ?? { color, width: 2, style: 'SOLID' }
            patchAll({ stroke: { ...existing, color } })
          }}
        />
      )}
      <div className="dh-ft-divider" />
      <ToolbarButton title="Duplicate (⌘D)" onClick={duplicate}>
        <ContentCopyOutlined fontSize="small" />
      </ToolbarButton>
      <ToolbarButton title="Delete" variant="danger" onClick={() => deleteShapes(selection)}>
        <DeleteOutlined fontSize="small" />
      </ToolbarButton>
    </div>
  )
}

const ToolbarButton = ({
  title,
  onClick,
  children,
  active = false,
  variant,
}: {
  title: string
  onClick: () => void
  children: React.ReactNode
  active?: boolean
  variant?: 'danger'
}) => (
  <IconButton
    variant="tertiary"
    size="sm"
    pressed={active}
    tone={variant === 'danger' ? 'danger' : undefined}
    onClick={onClick}
    label={title}
    icon={children}
  />
)

const ColorSwatch = ({
  title,
  value,
  icon,
  onChange,
}: {
  title: string
  value: string
  icon?: React.ReactNode
  onChange: (color: string) => void
}) => (
  <span className="dh-ft-swatch" title={title}>
    {icon}
    <ColorPicker size="sm" ariaLabel={title} value={value} onChange={onChange} />
  </span>
)
